import { useState } from "react";
import { useSettingsStore } from "../../store/settingsStore";
import { useDeviceSettingsStore } from "../../store/deviceSettingsStore";
import useWorkspaceStore, {
  getScopedItem,
  setScopedItem,
  removeScopedItem,
} from "../../store/workspaceStore";
import { getChartPreferenceKey } from "../../workspace/constants";
import { isFeatureEnabled } from "../../data/Services/settings";
import { isWebOnWindows } from "../../desktop/platform";

const VISIBILITY_OPTIONS = [
  { label: "Sempre", value: "always" },
  { label: "Nunca", value: "never" },
  { label: "Mouse", value: "hover" },
];

const CROSSHAIR_WIDTH_OPTIONS = [
  { label: "Fina", value: 1 },
  { label: "Média", value: 3 },
  { label: "Grossa", value: 5 },
];

const CHART_ADD_MODE_OPTIONS = [
  {
    label: "Abas",
    value: "tabs",
    tooltip: "Novos gráficos abrem como abas no painel ativo",
  },
  {
    label: "Grade",
    value: "grid",
    tooltip: "Novos gráficos reorganizam a tela em grade automática",
  },
  {
    label: "Inteligente",
    value: "smart",
    tooltip:
      "Até 6 gráficos: organiza em grade automática. Acima de 6: adiciona como abas",
  },
];

// Normalize crosshair width to valid values (1, 3, or 5)
const getNormalizedWidth = (width) => {
  if (!width) return 3;
  if (width <= 1.5) return 1;
  if (width <= 4) return 3;
  return 5;
};

/**
 * Declarative model of the Config modal: 4 groups (tabs), each item rendered
 * by its `type` in ConfigPanel:
 *   toggle — Checkbox + label
 *   choice — segmented button group (options[])
 *   theme  — theme selector row (self-contained component)
 *   action — full-width button (onClick)
 * Feature-flag / platform gating happens here (read at render time, never at
 * module scope). Items already filtered — ConfigPanel just renders.
 */
export const useConfigGroups = ({ onForceReload }) => {
  const confirmOrders = useSettingsStore((s) => s.confirmOrders);
  const routingSounds = useSettingsStore((s) => s.routingSounds);
  const oneClickTrading = useSettingsStore((s) => s.oneClickTrading);
  const showGrid = useSettingsStore((s) => s.showGrid);
  const showIndicatorLabels = useSettingsStore((s) => s.showIndicatorLabels);
  const zoomButtonsAlwaysVisible = useSettingsStore(
    (s) => s.zoomButtonsAlwaysVisible,
  );
  const barCountdownVisibilityMode = useSettingsStore(
    (s) => s.barCountdownVisibilityMode ?? "hover",
  );
  const priceChangeVisibilityMode = useSettingsStore(
    (s) => s.priceChangeVisibilityMode ?? "hover",
  );
  const crosshairWidth = useSettingsStore((s) => s.crosshairWidth);
  const autoShowLoadDesktopDialog = useSettingsStore(
    (s) => s.autoShowLoadDesktopDialog,
  );
  const keepScreenAwake = useSettingsStore((s) => s.keepScreenAwake);
  const showIchimokuFutureProjection = useSettingsStore(
    (s) => s.showIchimokuFutureProjection ?? true,
  );
  const defaultPanelToolbarPinned = useSettingsStore(
    (s) => s.defaultPanelToolbarPinned ?? true,
  );
  const tipOfTheDayEnabled = useSettingsStore(
    (s) => s.tipOfTheDayEnabled ?? true,
  );
  const showMyDesktopsBar = useSettingsStore(
    (s) => s.showMyDesktopsBar ?? true,
  );
  const oneLineNotifications = useSettingsStore(
    (s) => s.oneLineNotifications ?? false,
  );

  const setConfirmOrders = useSettingsStore((s) => s.setConfirmOrders);
  const setRoutingSounds = useSettingsStore((s) => s.setRoutingSounds);
  const setOneClickTrading = useSettingsStore((s) => s.setOneClickTrading);
  const setShowGrid = useSettingsStore((s) => s.setShowGrid);
  const setShowIndicatorLabels = useSettingsStore(
    (s) => s.setShowIndicatorLabels,
  );
  const setZoomButtonsAlwaysVisible = useSettingsStore(
    (s) => s.setZoomButtonsAlwaysVisible,
  );
  const setBarCountdownVisibilityMode = useSettingsStore(
    (s) => s.setBarCountdownVisibilityMode,
  );
  const setPriceChangeVisibilityMode = useSettingsStore(
    (s) => s.setPriceChangeVisibilityMode,
  );
  const setCrosshairWidth = useSettingsStore((s) => s.setCrosshairWidth);
  const setAutoShowLoadDesktopDialog = useSettingsStore(
    (s) => s.setAutoShowLoadDesktopDialog,
  );
  const setKeepScreenAwake = useSettingsStore((s) => s.setKeepScreenAwake);
  const setShowIchimokuFutureProjection = useSettingsStore(
    (s) => s.setShowIchimokuFutureProjection,
  );
  const setDefaultPanelToolbarPinned = useSettingsStore(
    (s) => s.setDefaultPanelToolbarPinned,
  );
  const setTipOfTheDayEnabled = useSettingsStore(
    (s) => s.setTipOfTheDayEnabled,
  );
  const setShowMyDesktopsBar = useSettingsStore((s) => s.setShowMyDesktopsBar);
  const setOneLineNotifications = useSettingsStore(
    (s) => s.setOneLineNotifications,
  );

  // "Barra de navegação com opções do gráfico" — per-DEVICE
  // (deviceSettingsStore, nunca sincronizado com o servidor); null = default
  // (ligado, controles visíveis).
  const navbarChartControlsPref = useDeviceSettingsStore(
    (s) => s.navbarChartControls,
  );
  const setNavbarChartControls = useDeviceSettingsStore(
    (s) => s.setNavbarChartControls,
  );

  const chartAddMode = useWorkspaceStore((s) => s.chartAddMode);
  const setChartAddMode = useWorkspaceStore((s) => s.setChartAddMode);

  // Chart preference (user-scoped localStorage, not a store setting)
  const [dontShowChartModal, setDontShowChartModal] = useState(
    () => getScopedItem(getChartPreferenceKey) === "tradezone-charts-26",
  );
  const handleChartPreferenceChange = (checked) => {
    setDontShowChartModal(checked);
    if (checked) {
      setScopedItem(getChartPreferenceKey, "tradezone-charts-26");
    } else {
      removeScopedItem(getChartPreferenceKey);
    }
  };

  const groups = [
    {
      id: "trading",
      label: "Negociação",
      items: [
        {
          id: "confirmOrders",
          type: "toggle",
          label: "Confirmações de ordens",
          value: confirmOrders,
          onChange: setConfirmOrders,
        },
        {
          id: "routingSounds",
          type: "toggle",
          label: "Sons de Roteamento",
          value: routingSounds,
          onChange: setRoutingSounds,
        },
        {
          // #430 — toasts compactos de uma linha p/ não cobrirem o book do
          // SuperDOM em ordens rápidas. Preferência de usuário (não é flag).
          id: "oneLineNotifications",
          type: "toggle",
          label: "Notificações em uma linha",
          value: oneLineNotifications,
          onChange: setOneLineNotifications,
          evoId: "navbar-config-one-line-notifications",
        },
        {
          id: "oneClickTrading",
          type: "toggle",
          label: "Negociações One Click (Shift e Control)",
          value: oneClickTrading,
          onChange: setOneClickTrading,
        },
      ],
    },
    {
      id: "chart",
      label: "Gráfico",
      items: [
        {
          id: "showGrid",
          type: "toggle",
          label: "Mostrar Grid",
          value: showGrid,
          onChange: setShowGrid,
        },
        {
          id: "showIndicatorLabels",
          type: "toggle",
          label: "Mostrar Rótulos de Indicadores",
          value: showIndicatorLabels,
          onChange: setShowIndicatorLabels,
        },
        {
          id: "showIchimokuFutureProjection",
          type: "toggle",
          label: "Projeção Futura do Ichimoku",
          value: showIchimokuFutureProjection,
          onChange: setShowIchimokuFutureProjection,
          evoId: "navbar-config-ichimoku-future-projection",
        },
        {
          id: "zoomButtonsAlwaysVisible",
          type: "toggle",
          label: "Botões de Zoom Sempre Visíveis",
          value: zoomButtonsAlwaysVisible,
          onChange: setZoomButtonsAlwaysVisible,
        },
        {
          id: "navbarChartControls",
          type: "toggle",
          label: "Barra de navegação com opções do gráfico",
          value: navbarChartControlsPref ?? true,
          onChange: setNavbarChartControls,
          evoId: "navbar-config-navbar-chart-controls",
        },
        {
          id: "barCountdownVisibility",
          type: "choice",
          label: "Visibilidade do Cronômetro",
          options: VISIBILITY_OPTIONS,
          value: barCountdownVisibilityMode,
          onChange: setBarCountdownVisibilityMode,
          evoId: "configpanel-btn-countdown-visibility",
        },
        {
          id: "priceChangeVisibility",
          type: "choice",
          label: "Visibilidade da Variação",
          options: VISIBILITY_OPTIONS,
          value: priceChangeVisibilityMode,
          onChange: setPriceChangeVisibilityMode,
          evoId: "configpanel-btn-price-change-visibility",
        },
        {
          id: "crosshairWidth",
          type: "choice",
          label: "Espessura do Cursor em Cruz",
          options: CROSSHAIR_WIDTH_OPTIONS,
          value: getNormalizedWidth(crosshairWidth),
          onChange: setCrosshairWidth,
          evoId: "configpanel-btn-crosshair-width",
        },
      ],
    },
    {
      id: "panels",
      label: "Painéis",
      items: [
        {
          id: "chartAddMode",
          type: "choice",
          label: "Modo de Adição de Painéis",
          options: CHART_ADD_MODE_OPTIONS,
          value: chartAddMode,
          onChange: setChartAddMode,
          evoId: "configpanel-btn-chart-add-mode",
        },
        {
          id: "defaultPanelToolbarPinned",
          type: "toggle",
          label: "Novos painéis com toolbar fixado",
          value: defaultPanelToolbarPinned,
          onChange: setDefaultPanelToolbarPinned,
          evoId: "navbar-config-checkbox-default-panel-toolbar-pinned",
        },
        {
          id: "showMyDesktopsBar",
          type: "toggle",
          label: "Mostrar barra Meus Desktops",
          value: showMyDesktopsBar,
          onChange: setShowMyDesktopsBar,
          evoId: "navbar-config-checkbox-my-desktops-bar",
          visible: isFeatureEnabled("myDesktops"),
        },
        {
          id: "autoShowLoadDesktopDialog",
          type: "toggle",
          label: "Mostrar diálogo de desktops ao iniciar",
          value: autoShowLoadDesktopDialog,
          onChange: setAutoShowLoadDesktopDialog,
          evoId: "navbar-config-checkbox-autoshow-desktop",
        },
      ],
    },
    {
      id: "general",
      label: "Geral",
      items: [
        {
          id: "theme",
          type: "theme",
        },
        {
          id: "keepScreenAwake",
          type: "toggle",
          label: "Manter Tela Ativa",
          value: keepScreenAwake,
          onChange: setKeepScreenAwake,
        },
        {
          id: "tipOfTheDayEnabled",
          type: "toggle",
          label: "Mostrar dica do dia",
          value: tipOfTheDayEnabled,
          onChange: setTipOfTheDayEnabled,
          evoId: "navbar-config-checkbox-tip-of-the-day",
        },
        {
          // O modal de seleção de gráficos só existe web+Windows
          // (AppRouter gate) — fora disso o checkbox seria um controle morto.
          id: "dontShowChartModal",
          type: "toggle",
          label: "Não mostrar seleção de gráficos no login",
          value: dontShowChartModal,
          onChange: handleChartPreferenceChange,
          evoId: "navbar-config-checkbox-chart-preference",
          visible: isWebOnWindows(),
        },
        {
          id: "forceReload",
          type: "action",
          label: "Forçar Recarregar sem Cache",
          icon: "pi pi-refresh",
          tooltip:
            "Recarrega a aplicação forçando o download de todos os arquivos (ignora cache). Sua autenticação será preservada.",
          onClick: onForceReload,
          evoId: "navbar-config-force-reload",
        },
      ],
    },
  ];

  return groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.visible !== false),
  }));
};
